import React, { useEffect, useState } from 'react';
import InputSearchCity, { PlaceSuggestion } from '../../../components/InputSearchCity';
import { plebsRepository } from '../../../repositories/PlebsRepository';
import { useAuth } from '../../../hooks/useAuth';
import { canManageMembers, formatDistance } from '../../../models/Pleb';
import Loader from './Loader';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../../hooks/useTheme';
import { SearchFilter, getSearchFilterName } from '../../../models/SearchFilter';
import SearchTilePleb from './SearchTilePleb';
import { eventsRepository } from '../../../repositories/EventsRepository';
import { getDistanceBetweenCoordinateAndEvent } from '../../../models/Event';
import SearchTileEvent from './SearchTileEvent';
import { useInView } from 'react-intersection-observer';
import { Button, ButtonSize } from '../../../components/Button';
import { ButtonVariant } from '../../../config/colors';
import { TitleBar } from '../../../components/TitleBar';

interface SearchProps {
  
}

const Search: React.FC<SearchProps> = () => {  
  const {pleb} = useAuth();
  const {tab} = useParams();
  const navigate = useNavigate();
  const [suggestion, setSuggestion] = useState<PlaceSuggestion | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [errorUsers, setErrorUsers] = useState<string | null>(null);
  const [errorEvents, setErrorEvents] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<SearchFilter>(SearchFilter.PEOPLE);
  const {isMobile} = useTheme();
  const [pageEvents, setPageEvents] = useState(0);
  const [pageUsers, setPageUsers] = useState(0);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [countUsers, setCountUsers] = useState(0);
  const [countEvents, setCountEvents] = useState(0);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const { ref, inView } = useInView();
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    setUsers([]);
    setEvents([]);
    setHasMoreUsers(true);
    setHasMoreEvents(true);
    setPageUsers(0);
    setPageEvents(0);
    setCountUsers(0);
    setCountEvents(0);
  }, [suggestion]);
  

  useEffect(() => {
    setIsLoadingUsers(true);
    setIsLoadingEvents(true);
    setErrorUsers(null);
    setErrorEvents(null);
    
    if(suggestion === null) {
      setIsLoadingUsers(false);
      setIsLoadingEvents(false);
      return;
    }

    if(selectedFilter === SearchFilter.PEOPLE || pageUsers === 0) {
      plebsRepository.getUsersNearby(
        suggestion.coordinates?.latitude ?? pleb?.coordinates?.latitude ?? 0, 
        suggestion.coordinates?.longitude ?? pleb?.coordinates?.longitude ?? 0, 
        suggestion?.filter ?? null,
        pleb?.id ?? '',
        pageUsers,
        ITEMS_PER_PAGE
      )
        .then((response) => {
          setCountUsers(response.count);
          const newUsers = response.data;

          if (newUsers.length < ITEMS_PER_PAGE) {
            setHasMoreUsers(false);
          }
          setUsers(prev => pageUsers === 0 ? newUsers : [...prev, ...newUsers]);
          if (pageUsers === 0 && newUsers.length === 0) {
            setErrorUsers('No Bitcoiners found in this area');
          }
        })
        .catch((error) => {
          console.error(error);
          setCountUsers(0);
          setErrorUsers('Failed to fetch nearby users');
        })
        .finally(() => {
          setIsLoadingUsers(false);
          setSelectedFilterIfEmpty();
        }); 
    }

    if(selectedFilter === SearchFilter.EVENTS || pageEvents === 0) {
      eventsRepository.getEventsNearby(
        suggestion.coordinates?.latitude ?? pleb?.coordinates?.latitude ?? 0, 
        suggestion.coordinates?.longitude ?? pleb?.coordinates?.longitude ?? 0,
        suggestion?.filter ?? null,
        pleb?.id ?? '',
        pageEvents,
        ITEMS_PER_PAGE
      )
        .then((response) => {
          setCountEvents(response.count);
          const newEvents = response.data;

          if (newEvents.length < ITEMS_PER_PAGE) {
            setHasMoreEvents(false);
          }
          setEvents(prev => pageEvents === 0 ? newEvents : [...prev, ...newEvents]);
          if (pageEvents === 0 && newEvents.length === 0) {
            setErrorEvents('No Events found in this area');
          }
        })
        .catch((error) => {
          console.error(error);
          setCountEvents(0);
          setErrorEvents('Failed to fetch nearby events');
        })
        .finally(() => {
          setIsLoadingEvents(false);
          setSelectedFilterIfEmpty();
        });
    }
  }, [suggestion, pleb?.id, pageUsers, pageEvents]);

  useEffect(() => {
    if (inView && hasMoreUsers && !isLoadingUsers && suggestion && selectedFilter === SearchFilter.PEOPLE) {
      setPageUsers(prev => prev + 1);
    }
    if (inView && hasMoreEvents && !isLoadingEvents && suggestion && selectedFilter === SearchFilter.EVENTS) {
      setPageEvents(prev => prev + 1);
    }
  }, [inView, isLoadingUsers, isLoadingEvents, suggestion, hasMoreUsers, hasMoreEvents, selectedFilter]);

  useEffect(() => {
    setSelectedFilterIfEmpty();
  }, [isLoadingUsers, isLoadingEvents, countUsers, countEvents]);

  const setSelectedFilterIfEmpty = () => {
    if(
        (!isLoadingUsers || countUsers > 0) && 
        (!isLoadingEvents || countEvents > 0) && 
        selectedFilter === SearchFilter.PEOPLE && 
        countUsers === 0 && 
        countEvents > 0
    ) {
      setSelectedFilter(SearchFilter.EVENTS);
    }
    if(
      (!isLoadingUsers || countUsers > 0) && 
      (!isLoadingEvents || countEvents > 0) && 
      selectedFilter === SearchFilter.EVENTS && 
      countEvents === 0 && 
      countUsers > 0
    ) {
      setSelectedFilter(SearchFilter.PEOPLE);
    }
  }

  const handlePlaceSelect = (suggestion: PlaceSuggestion | null) => {
    setSuggestion(suggestion);
  }

  const handleSelectInput = (input: string) => {
    setSuggestion({
      type: 'subscription',
      name: input,
      coordinates: undefined,
      filter: input
    });
  }

  const renderPeople = () => {
    return (
      <>
        {isLoadingUsers && users.length === 0 && (
          <Loader />
        )}

        {errorUsers && (
          <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} px-4 py-4 h-full`}>
            <p className="text-center text-base opacity-50">
              {errorUsers}
            </p>
          </div>
        )}

        {selectedFilter === SearchFilter.PEOPLE && !errorUsers && users.length > 0 && (
          <div>
            {users.map((user, index) => {
              return <SearchTilePleb 
                key={user.id}
                pleb={user} 
                index={index} 
                onTap={() => navigate(`/home/${tab}/user/${user.id}`)} 
                distance={user.distance ? formatDistance(user.distance) : null} 
                allowPressAwaitingReply={(pleb && canManageMembers(pleb)) ? true : false}
              />
            })}
            {isLoadingUsers && <div className="flex justify-center items-center py-4"><Loader small /></div>}
            {hasMoreUsers && <div ref={ref} className="h-5" />}
          </div>
        )}
      </>
    );
  }

  const renderEvents = () => {
    return (
      <>
        {isLoadingEvents && events.length === 0 && (
          <Loader />
        )}

        {errorEvents && (
          <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} px-4 py-4 h-full`}>
            <p className="text-center text-base opacity-50">
              {errorEvents}
            </p>
          </div>
        )}

        {selectedFilter === SearchFilter.EVENTS && !errorEvents && events.length > 0 && (
          <div>
            {events.map((event, index) => {
              const distance = suggestion && suggestion.coordinates ? getDistanceBetweenCoordinateAndEvent(suggestion.coordinates, event) : null;

              return <SearchTileEvent 
                key={event.id}
                event={event} 
                index={index} 
                onTap={() => navigate(`/home/${tab}/event/${event.id}`)} 
                distance={distance} 
              />
            })}
            {isLoadingEvents && <div className="flex justify-center items-center py-4"><Loader small /></div>}
            {hasMoreEvents && <div ref={ref} className="h-5" />}
          </div>
        )}
      </>
    );
  }
  
  return (
    <div className="flex-1 pt-8 overflow-hidden flex flex-col gap-4">
      <TitleBar title="Search" className="px-4 pb-4" />

      <div className="px-4">
        <InputSearchCity
          placeholder="Search Bitcoiners & Events by city"
          onPlaceSelect={handlePlaceSelect}
          onPressEnter={handleSelectInput}
        />
      </div>

      {
        suggestion && (
          <div className="px-4">
            <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
            {
              Object.values(SearchFilter).map((filter) => (
                <Button 
                  key={filter} 
                  variant={filter === selectedFilter ? ButtonVariant.inverted : ButtonVariant.unselected} 
                  size={ButtonSize.medium}
                  onClick={() => {
                    setSelectedFilter(filter);
                  }}
                  className="flex-1"
                >
                  {getSearchFilterName(filter)} 
                  {filter === SearchFilter.PEOPLE && countUsers > 0 && ` (${countUsers})`}
                  {filter === SearchFilter.EVENTS && countEvents > 0 && ` (${countEvents})`}
                </Button>
              ))
            }
            </div>
          </div>
        )
      }

      <div className="flex-1 overflow-y-auto">
        {selectedFilter === SearchFilter.PEOPLE && renderPeople()}
        {selectedFilter === SearchFilter.EVENTS && renderEvents()}
      </div>
    </div>
  );
};

export default Search;